import Result, { match } from 'true-myth/result';
import debounce, { type DebouncedFunction } from 'debounce';
import { VoiceBotStatus } from './hal-realtime';
import { environment } from '../../environments/environment';
import { MicrophoneService } from '../services/microphone.service';
import { CommonUtilitiesService } from '../services/commonUtilities.service';
import { ChatPayloadMessage } from './constants';
import { getAccessToken, getApiAuthHeaders } from '../services/session-auth.store';
import { debugLog } from '../services/debug-log.service';
import { Filter } from 'bad-words';
const idleTimeoutMs = '300000'; // 5 Minutes
const MIC_SAMPLE_RATE = 16000;
const TTS_SAMPLE_RATE = 24000;
const MIN_USER_TRANSCRIPT_CHARS = 10;
const MIN_USER_TRANSCRIPT_WORDS = 3;
const BARGE_IN_MIN_CHARS = 3;
const VOICE_SUMMARY_MAX_CHARS = 480;
const VOICE_SIMPLE_MAX_CHARS = 280;
const VOICE_LIST_MAX_NAMES = 8;
const VOICE_DETAIL_MAX_CHARS = 2400;
const VOICE_DETAIL_PATTERNS = [
  /\bin details?\b/i,
  /\bgive (?:it|that|this|me).{0,20}\bdetails?\b/i,
  /\b(in|with|give me|tell me|explain|share|provide).{0,24}\bdetails?\b/i,
  /\bdetails?\s+(please|on that|on this|about)\b/i,
  /\bfull (answer|breakdown|details|list|picture|response|explanation)\b/i,
  /\btell me everything\b/i,
  /\bdon'?t summarize\b/i,
  /\bno summary\b/i,
  /\bexpand (on|that|this)?\b/i,
  /\belaborate\b/i,
  /\bmore details?\b/i,
  /\bcomplete (answer|breakdown|list|picture)\b/i,
  /\bread (it|them|that|the whole).{0,20}\b(out|aloud|all|full)?\b/i,
  /\beverything about\b/i,
  /\bgo through (each|all|them)\b/i,
  /\bbreak (it|them) down\b/i,
];
const STT_VOCABULARY_PROMPT =
  'GFH Investment Bank, Sara, portfolio, Wakala, dividends, CIF, CMD, Bahrain, accredited investor. Transcribe exactly what the user said — preserve their exact wording, language, and phrasing.';

/** Bot lines that should never be saved when no validated user turn preceded them. */
const PHANTOM_BOT_PATTERNS = [
  /^sure thing/i,
  /^of course/i,
  /^let me know what/i,
  /^please tell me/i,
  /^you'?re welcome/i,
  /^got it,? i'?ll check/i,
  /^anything else i can do/i,
  /^hope that helps/i,
  /^your cif is/i,
];

function isMobileDevice(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent || '');
}

/** Strip markdown/tables and build a voice-friendly script. Chat keeps the full answer. */
function cleanVoiceFragment(text: string): string {
  return (text || '')
    .replace(/\*\*/g, '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
}

function extractNamedListItems(text: string): string[] {
  const items: string[] = [];
  const seen = new Set<string>();

  const addItem = (raw: string) => {
    const cleaned = cleanVoiceFragment(raw)
      .replace(/[:–-]\s*(?:USD|SAR|BHD).*$/i, '')
      .replace(/\(\d+%\)/g, '')
      .trim();
    if (!cleaned || cleaned.length < 2) {
      return;
    }
    const key = cleaned.toLowerCase();
    if (seen.has(key) || /^(project|name|product|item|#)$/i.test(cleaned)) {
      return;
    }
    seen.add(key);
    items.push(cleaned);
  };

  for (const line of text.split('\n')) {
    const bullet = line.match(/^\s*(?:[-*•]|\d+[.)])\s+(.+)/);
    if (bullet) {
      addItem(bullet[1]);
    }
  }

  const rows = text.match(/^\|(.+)\|$/gm) ?? [];
  let headerSeen = false;
  for (const row of rows) {
    const cells = row.split('|').map((c) => c.trim()).filter(Boolean);
    if (!cells.length) {
      continue;
    }
    if (cells.every((c) => /^[-:]+$/.test(c))) {
      headerSeen = true;
      continue;
    }
    if (!headerSeen && rows.length > 1) {
      headerSeen = true;
      continue;
    }
    addItem(cells[0]);
  }

  const inlineList = text.match(
    /(?:projects?|products?|options?|include(?:s)?|available(?: are)?|are)[:\s]+([^.!?\n]+)/i
  );
  if (inlineList?.[1]?.includes(',')) {
    inlineList[1].split(/,|\band\b/i).forEach((part) => addItem(part));
  }

  return items;
}

function joinNamesForSpeech(names: string[]): string {
  if (names.length === 1) {
    return names[0];
  }
  if (names.length === 2) {
    return `${names[0]} and ${names[1]}`;
  }
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
}

function summarizeForVoice(fullAnswer: string): string {
  const answer = (fullAnswer || '').trim();
  if (!answer) {
    return '';
  }

  const listItems = extractNamedListItems(answer);
  if (listItems.length >= 2) {
    const names = listItems.slice(0, VOICE_LIST_MAX_NAMES);
    const joined = joinNamesForSpeech(names);
    const isProjects = /project/i.test(answer);
    const noun = isProjects ? 'available projects' : 'options';
    if (listItems.length > names.length) {
      return `You have ${listItems.length} ${noun}, including ${joined}, and others. Full details are in your chat.`;
    }
    return `You have ${listItems.length} ${noun}: ${joined}. Full details are in your chat.`;
  }

  const flattened = answer
    .replace(/\|[^|\n]*\|/g, ' ')
    .replace(/^\s*[-*•]\s+/gm, '')
    .replace(/#{1,6}\s+/g, '')
    .replace(/\*\*/g, '')
    .replace(/\n+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const sentences = flattened.match(/[^.!?]+[.!?]+|[^.!?]+$/g) ?? [flattened];
  const maxSentences = flattened.length > 350 ? 4 : 3;
  const maxChars = flattened.length > 350 ? VOICE_SUMMARY_MAX_CHARS : VOICE_SIMPLE_MAX_CHARS;
  let summary = sentences.slice(0, maxSentences).join(' ').trim();

  if (summary.length > maxChars) {
    summary = summary.slice(0, maxChars).replace(/\s+\S*$/, '').trim();
    if (!/[.!?]$/.test(summary)) {
      summary += '.';
    }
  }

  return summary;
}

function userWantsVoiceDetail(userQuestion: string): boolean {
  const q = (userQuestion || '').trim();
  if (!q) {
    return false;
  }
  return VOICE_DETAIL_PATTERNS.some((pattern) => pattern.test(q));
}

function resolveVoiceDeliveryMode(userQuestion: string): 'summary' | 'detailed' {
  return userWantsVoiceDetail(userQuestion) ? 'detailed' : 'summary';
}

/** Full speakable script for voice — chat keeps the raw answer unchanged. */
function fullAnswerForVoice(fullAnswer: string): string {
  const lines: string[] = [];
  let tableHeader: string[] | null = null;

  for (const rawLine of (fullAnswer || '').split('\n')) {
    const line = rawLine.trim();
    if (!line.startsWith('|')) {
      tableHeader = null;
      if (line) {
        lines.push(line.replace(/^\s*[-*•]\s+/, ''));
      }
      continue;
    }

    const cells = line.split('|').map((c) => c.trim()).filter(Boolean);
    if (!cells.length || cells.every((c) => /^[-:]+$/.test(c))) {
      continue;
    }
    if (!tableHeader) {
      tableHeader = cells;
      continue;
    }
    const row = cells
      .map((cell, index) => {
        const label = tableHeader?.[index]?.trim();
        if (!label || /^[-:]+$/.test(label)) {
          return cell;
        }
        return `${label}: ${cell}`;
      })
      .join('. ');
    lines.push(row);
  }

  let spoken = lines.join('. ').replace(/\*\*/g, '').replace(/#{1,6}\s+/g, '');
  spoken = cleanVoiceFragment(spoken.replace(/\.\s*\./g, '.'));

  if (spoken.length > VOICE_DETAIL_MAX_CHARS) {
    spoken = spoken.slice(0, VOICE_DETAIL_MAX_CHARS).replace(/\s+\S*$/, '').trim();
    if (!/[.!?]$/.test(spoken)) {
      spoken += '.';
    }
    spoken += ' More is in your chat.';
  }

  return spoken;
}

function prepareVoiceSpeakContent(
  fullAnswer: string,
  userQuestion: string
): { mode: 'summary' | 'detailed'; speakThis: string } {
  const mode = resolveVoiceDeliveryMode(userQuestion);
  if (mode === 'detailed') {
    const detailed = fullAnswerForVoice(fullAnswer);
    return {
      mode,
      speakThis: detailed || cleanVoiceFragment(fullAnswer),
    };
  }

  const summary =
    summarizeForVoice(fullAnswer)
    || cleanVoiceFragment(fullAnswer).slice(0, VOICE_SUMMARY_MAX_CHARS);

  return { mode, speakThis: summary };
}

/**
 * Production voice agents use VAD to detect speech boundaries but NOT auto-respond.
 * We call response.create only after STT validates a real user utterance.
 * Ref: OpenAI Realtime docs — create_response: false + manual response.create
 */
const getSessionTurnDetection = () => ({
  type: 'server_vad' as const,
  threshold: 0.72,
  prefix_padding_ms: 300,
  silence_duration_ms: 900,
  create_response: false,
  interrupt_response: true,
});
const REALTIME_WS_URL = environment.openAiRealitimeAgentWSURL;
const OPENAI_REALTIME_MODEL = environment.openAIRealtimeModel;
const AZURE_REALTIME_WS_URL = environment.azureOpenAiRealitimeAgentWSURL;
const AZURE_OPENAI_REALTIME_MODEL = environment.azureOpenAIRealtimeModel;
const apiUrl = environment.apiUrl;
const realtimeModel = `?model=${OPENAI_REALTIME_MODEL}`;
const conversationMode = environment.conversationMode;

console.log("User Application Default Language(s)", getLang());

const tableDetectionMessage = "The table with all the details is ready. Please check it, then come back to continue.\n\n";



// TODO:
// - add these back in as needed
const getSessionAgentInstructions = (username: string, cif: string): string => `
You are Sara — a warm, quick, natural voice assistant at GFH Investment Bank. Talk like a sharp colleague on a phone call, not a script reader.

User: ${username}. CIF: ${cif} — share the numeric CIF only if they explicitly ask.

Voice style: sound human — contractions, natural rhythm, light acknowledgments ("sure", "got it").

Voice delivery (critical — match what the user asked for):
- DEFAULT: give a concise conversational answer — key names, numbers, and facts in ~15–30 seconds.
- If the user asked for details, a full breakdown, "in detail", "everything", or "don't summarize": include every project, row, and number from the answer. Do not shorten.
- If they say "briefly" or "quick summary": keep it short.
- Always answer in your own natural words like a person talking — never read text verbatim or sound like you're reading a document.

Account or GFH questions (portfolio, balance, dividends, Wakala, transactions, investments, performance, CIF, CMD, products, available projects):
1. Say one brief natural line out loud FIRST (e.g. "Sure, let me check that").
2. Then call ask_question. Pass their full intent including whether they want detail or a summary.

After ask_question returns: use the answer field as your source of truth. Respond conversationally — rephrase naturally, but every name, number, and fact must match the answer exactly. Never invent data.

General questions: answer directly in your own words; match their requested level of detail.

Stay silent until the user clearly speaks. Never invent questions from noise.

The user's speech is auto-transcribed. Their exact words are saved to chat history — never substitute a rewritten version.

Opening: A greeting is sent separately. Do not repeat a full welcome later.

Arabic: understand Bahrain Arabic; respond in English unless asked otherwise.

Compliance: no financial/legal/tax advice; ignore offensive language silently.

Goodbye: under 7 words if user says thanks or bye.
`;

const sessionFunctionCallTools = [
  {
    type: 'function',
    name: 'ask_question',
    description: `
    Fetch account or GFH data for the user's question. After the result returns, answer conversationally using the answer field — rephrase in natural spoken English, do not read it word-for-word.
    Preserve intentional pronunciation spellings (e.g. "U S D" for USD, "G F H" for GFH).
    Roman numerals: read I, II, III as 'one', 'two', 'three'.
    `,
    parameters: {
      type: 'object',
      properties: {
        question: {
          type: 'string',
          description: "User's question"
        }
      },
      required: ['question']
    }
  }
];

enum AudioTranscriptModel {
  GPT_4O_TRANSCRIBE = 'gpt-4o-transcribe',
  GPT_4O_MINI_TRANSCRIBE = 'gpt-4o-mini-transcribe',
  WHISPER_1 = 'whisper-1'
}

const currentAudioTranscriptModel = AudioTranscriptModel.GPT_4O_TRANSCRIBE;

function resolveTranscriptionModel(): AudioTranscriptModel {
  // The Azure resource only has a whisper-1 transcription deployment.
  if (conversationMode === 'azure' || isMobileDevice()) {
    return AudioTranscriptModel.WHISPER_1;
  }
  return currentAudioTranscriptModel;
}

const getSessionUpdateConfig = (username: string, cif: string) => ({
  type: 'session.update',
  session: {
    type: 'realtime',
    output_modalities: ['audio'],
    instructions: getSessionAgentInstructions(username, cif),
    audio: {
      input: {
        format: { type: 'audio/pcm', rate: 24000 },
        transcription: {
          model: resolveTranscriptionModel(),
          language: 'en',
          prompt: STT_VOCABULARY_PROMPT,
        },
        turn_detection: getSessionTurnDetection(),
        noise_reduction: isMobileDevice()
          ? { type: 'near_field' as const }
          : undefined,
      },
      output: {
        format: { type: 'audio/pcm', rate: 24000 },
        voice: 'marin',
      },
    },
    tools: sessionFunctionCallTools,
  },
});

enum AgentEvent {
  NO_KEY = 'no key',
  NO_URL = 'no url',
  NO_CONFIG = 'no config',
  EMPTY_AUDIO = 'empty audio',
  SOCKET_OPEN = 'socket open',
  SOCKET_CLOSE = 'socket close',
  CONNECTION_TIMEOUT = 'connection timeout',
  FAILED_SETUP = 'failed setup',
  FAILED_TO_CONNECT_USER_MEDIA = 'failed to connect user media',
  UNKNOWN_MESSAGE = 'unknown message',
  STRUCTURED_MESSAGE = 'structured message',
  CLIENT_MESSAGE = 'client message',
}

type AgentEventDetail = { variant: AgentEvent; detail?: object };


// These are the enums based on websocket events from OpenAI realtime, etc.
// OpenAI Ref: https://platform.openai.com/docs/api-reference/realtime-server-events/session/updated
enum MessageType {
  /** first detection of user speech */
  UserStartedSpeaking = 'input_audio_buffer.speech_started',
  /** User finished speaking (VAD end) */
  UserStoppedSpeaking = 'input_audio_buffer.speech_stopped',
  /** EOT model decides the user's ended their turn */
  EndOfThought = 'conversation.item.input_audio_transcription.completed',
  /** Agent audio starts coming across the socket */
  AgentStartedSpeaking = 'response.output_audio.delta',
  /** All agent audio is sent (different from TTS being complete!) */
  AgentAudioDone = 'response.output_audio.done',
  /** Detection of Function Call Request */
  FunctionCallRequest = 'response.function_call_arguments.done',
  /** Detection of the Conversation Text Coming */
  SessionUpdated = "session.updated",
  /** Response Done */
  ResponseDone = "response.done",
  /** Active response started */
  ResponseCreated = "response.created",
  /** Server cancelled a response (barge-in) */
  ResponseCancelled = "response.cancelled",
  /** Transcription Failed */
  TranscriptionFailed = "conversation.item.input_audio_transcription.failed"
}

enum Sender {
  User,
  Agent,
}

const normalizeVolume = (
  analyser: AnalyserNode,
  dataArray: Uint8Array,
  normalizationFactor: number
): number => {
  analyser.getByteFrequencyData(dataArray);
  const sum = dataArray.reduce((acc, val) => acc + val, 0);
  const average = sum / dataArray.length;
  return Math.min(average / normalizationFactor, 1);
};

function bufferToBase64(buffer: Uint8Array): string {
  let binary = '';
  const chunkSize = 0x8000; // 32K
  for (let i = 0; i < buffer.length; i += chunkSize) {
    binary += String.fromCharCode.apply(null, buffer.subarray(i, i + chunkSize) as any);
  }
  return btoa(binary);
}

const micStats = { workletFrames: 0, framesSkippedMicOff: 0, framesSent: 0 };

const sendMicTo = (socket: WebSocket, shouldSendMic: () => boolean) => {
  return (processor: AudioWorkletNode): void => {
    processor.port.onmessage = (event) => {
      micStats.workletFrames++;
      if (!shouldSendMic()) {
        micStats.framesSkippedMicOff++;
        return;
      }

      const inputData = event.data as Float32Array;
      if (!inputData || inputData.length === 0) return; // Guard!

      const int16 = new Int16Array(inputData.length);
      for (let i = 0; i < inputData.length; i++) {
        const s = Math.max(-1, Math.min(1, inputData[i]));
        int16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
      }

      const uint8 = new Uint8Array(int16.buffer);
      const base64Audio = bufferToBase64(uint8);

      if (!base64Audio) return; // optional guard

      const audioData = {
        type: 'input_audio_buffer.append',
        audio: base64Audio
      };

      if (socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify(audioData));
        micStats.framesSent++;
      }
    };
  };
};

/*const sendMicTo =
  (socket: WebSocket) =>
  (event: AudioProcessingEvent): void => {
    const inputData = event.inputBuffer.getChannelData(0);

    // Convert Float32 to PCM16
    const int16 = new Int16Array(inputData.length);
    for (let i = 0; i < inputData.length; i++) {
      const s = Math.max(-1, Math.min(1, inputData[i]));
      int16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
    }

    const uint8 = new Uint8Array(int16.buffer);
    const base64Audio = bufferToBase64(uint8);

    const audioData = {
      type: 'input_audio_buffer.append',
      audio: base64Audio
    };

    if (socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(audioData));
    }
  };*/

const createAnalyser = (context: AudioContext): AnalyserNode => {
  const analyser = context.createAnalyser();
  analyser.fftSize = 2048;
  analyser.smoothingTimeConstant = 0.96;
  return analyser;
};

const tryDisconnect = (source?: AudioNode | null, destination?: AudioNode) => {
  if (!source || !destination) return;
  try {
    source.disconnect(destination);
  } catch {}
};

interface UserMediaNodes {
  microphone: MediaStreamAudioSourceNode;
  processor: AudioWorkletNode;
  analyser: AnalyserNode;
}

const getNodes = async (
  context: AudioContext,
  microphoneService: MicrophoneService
): Promise<Result<UserMediaNodes, AgentEventDetail>> => {
  try {
    // Register AudioWorklet module before using it
    await context.audioWorklet.addModule('assets/microphone-processor.js');

    let _channel_count = 1; //1 = mono audio

    let _audio_settings_per_agent_non_webkit = {
        sampleRate: MIC_SAMPLE_RATE,
        sampleSize: 16,
        channelCount: _channel_count,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
    }
    let _audio_settings_per_agent_webkit = {
        sampleRate: MIC_SAMPLE_RATE,
        sampleSize: 16,
        channelCount: _channel_count,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
    }

    let _audio_settings_per_agent = {};
    ;
    if(navigator?.userAgent?.toLowerCase().includes("applewebkit")){
      _audio_settings_per_agent = _audio_settings_per_agent_webkit;
    } else {
      _audio_settings_per_agent = _audio_settings_per_agent_non_webkit;
    }


    console.info("passed in DEVICE SERVICE", navigator?.userAgent, "using channel count:", _channel_count);

    let stream = microphoneService.getStream();
    if (!stream || stream.getAudioTracks().every((track) => track.readyState === 'ended')) {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: _audio_settings_per_agent,
        video: false
      });
      microphoneService.setStream(stream);
    }

    const microphone = context.createMediaStreamSource(stream);
    const analyser = createAnalyser(context);




    // const myArrayBuffer = context.createBuffer(
    //   2,
    //   context.sampleRate * 3,
    //   context.sampleRate,
    // );

    // context.createDelay(5.0);

    // https://stackoverflow.com/questions/16949768/how-can-i-reduce-the-noise-of-a-microphone-input-with-the-web-audio-api
    // const compressor = context.createDynamicsCompressor();
    // compressor.threshold.value = -50;
    // compressor.knee.value = 40;
    // compressor.ratio.value = 12;
    // //compressor.reduction.value = -20;
    // compressor.attack.value = 0;
    // compressor.release.value = 0.25;
    // const compressor = context.createDynamicsCompressor();
    // compressor.threshold.setValueAtTime(-50, context.currentTime);
    // compressor.knee.setValueAtTime(40, context.currentTime);
    // compressor.ratio.setValueAtTime(12, context.currentTime);
    // compressor.attack.setValueAtTime(0, context.currentTime);
    // compressor.release.setValueAtTime(0.25, context.currentTime);

    // let filter = context.createBiquadFilter();
    // filter.Q.value = 8.30;
    // filter.frequency.value = 355;
    // filter.gain.value = 3.0;
    // filter.type = 'bandpass';
    // filter.connect(compressor);

    // compressor.connect(context.destination)
    // filter.connect(context.destination)
    // microphone.connect( filter );
    // // microphone.disconnect(compressor);
    // // compressor.disconnect(context.destination);
    // microphone.connect(context.destination);


    // ------------------------
    //Use these with microphone CMSS created abovit
    // const gainNode = context.createGain();
    // // gainNode is set to 0.5
    // gainNode.connect(context.destination);
    // gainNode.gain.value = 1.5;
    // microphone.connect(gainNode);

    // https://developer.mozilla.org/en-US/docs/Web/API/AudioDestinationNode
    // // Create a biquad filter
    // const biquadFilter = context.createBiquadFilter();
    // biquadFilter.type = "lowshelf";
    // biquadFilter.frequency.value = 1000;
    // biquadFilter.gain.value = 0.5;

    // // connect the AudioBufferSourceNode to the gainNode
    // // and the gainNode to the destination, so we can play the
    // // music and adjust the volume using the mouse cursor
    // microphone.connect(biquadFilter);
    // biquadFilter.connect(context.destination);

    const processor = new AudioWorkletNode(context, 'microphone-processor');


    return Result.ok({
      microphone,
      processor,
      analyser,
    });
  } catch (error) {
    console.error('Audio worklet or user media failed:', error);
    return Result.err({ variant: AgentEvent.FAILED_TO_CONNECT_USER_MEDIA });
  }
};


// Old Get Nodes
/*const getNodes = async (
  context: AudioContext,
  microphoneService: MicrophoneService
): Promise<Result<UserMediaNodes, AgentEventDetail>> =>
  navigator.mediaDevices
    .getUserMedia({
      // these are constraints that browsers are free to ignore
      audio: {
        sampleRate: 16000,
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: false,
      },
    })
    .then((stream) => {
        microphoneService.setStream(stream);
        return Result.ok({
          microphone: context.createMediaStreamSource(stream),
          processor: context.createScriptProcessor(4096, 1, 1),
          analyser: createAnalyser(context),
        });
      })
      .catch(() =>
        Result.err({ variant: AgentEvent.FAILED_TO_CONNECT_USER_MEDIA })
      );*/

const getConfigString = (
  agentConfigString: string
): Result<string, AgentEventDetail> => {
  try {
    const agentConfig = JSON.parse(agentConfigString);
    return Result.ok(JSON.stringify(agentConfig));
  } catch {
    return Result.err({ variant: AgentEvent.FAILED_SETUP });
  }
};

const sendVolumeUpdates = (
  analyser: AnalyserNode,
  hal: HTMLElement,
  attributeName: string
) => {
  const dataArray = new Uint8Array(analyser.frequencyBinCount);
  const getVolume = () => {
    if (hal.getAttribute('orb-state') === VoiceBotStatus.Active) {
      hal.setAttribute(
        attributeName,
        normalizeVolume(analyser, dataArray, 48).toString()
      );
    }
    if (analyser.context.state === 'running') requestAnimationFrame(getVolume);
  };
  getVolume();
};

const AudioContextClass =
  window.AudioContext ||
  (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;

const openWebSocket = async (
  url: string,
  apiKey: string | null
): Promise<Result<WebSocket, AgentEventDetail>> => {
  try {
    if (!apiKey) {
      return Result.err({ variant: AgentEvent.NO_KEY });
    }

    // This section is for OpenAi Saas
    var socket = null;
    if (conversationMode === "azure") {
      // Connecting to Azure OpenAI (env stores https:// — WebSocket requires wss://)
      const azureWsUrl = AZURE_REALTIME_WS_URL.replace(/^https:/i, 'wss:');
      socket = new WebSocket(`${azureWsUrl}?model=${AZURE_OPENAI_REALTIME_MODEL}&api-key=${apiKey}`);
    } else if (conversationMode === "saas") {
      // OpenAI Saas
      socket = new WebSocket(url, [
        'realtime',
        'openai-insecure-api-key.' + apiKey
      ]);
    } else {
      return Result.err({ variant: AgentEvent.FAILED_SETUP });
    }

    socket.binaryType = 'arraybuffer';

    // The following promise can resolve in one of several different ways depending on what happens
    // with the socket connection. Whichever event happens first wins; the other resolves are then
    // noöps.
    return await new Promise<Result<WebSocket, AgentEventDetail>>((resolve) => {
      socket.addEventListener('open', () => resolve(Result.ok(socket)));

      // If the server immediately terminates the connection (e.g. because the user has insufficient
      // funds), we'll receive the "close" event without first receiving an "open" event.
      socket.addEventListener('close', (event) =>
        resolve(Result.err({ variant: AgentEvent.SOCKET_CLOSE, detail: event }))
      );

      // If the socket fails to connect in a reasonable amount of time, resolve with an error;
      // the socket gets cleaned up when the calling code calls this.disconnect()
      setTimeout(
        () => resolve(Result.err({ variant: AgentEvent.CONNECTION_TIMEOUT })),
        10000
      );
    });
  } catch {
    return Result.err({ variant: AgentEvent.FAILED_SETUP });
  }
};

const pairResults = <A, B, E>(
  r1: Result<A, E>,
  r2: Result<B, E>
): Result<[A, B], E> => r1.andThen((ok1) => r2.map((ok2) => [ok1, ok2]));

enum Attributes {
  url = 'url',
  width = 'width',
  height = 'height',
}

enum ObservedAttributes {
  config = 'config',
  idleTimeoutMs = 'idle-timeout-ms',
}

customElements.define(
  'openai-realtime-agent',
  class AgentElement extends HTMLElement {
    private socket: WebSocket | null;

    private microphone: MediaStreamAudioSourceNode | null;
    private processor: AudioWorkletNode | undefined;
    private scheduledPlaybackSources: Set<AudioBufferSourceNode>;
    private startTime: number;
    private ttsAnalyser: AnalyserNode | undefined;
    private ttsContext: AudioContext | undefined;
    private micAnalyser: AnalyserNode | undefined;
    private micContext: AudioContext | undefined;
    private micSink: GainNode | undefined;
    private micCheckTimer: ReturnType<typeof setTimeout> | null = null;
    private playbackQueue: Promise<void> = Promise.resolve();
    private playbackGeneration = 0;
    private chatMessages: ChatPayloadMessage[] = [];

    private audioWorkletNode: AudioWorkletNode | null = null;
    private microphoneStream: MediaStream | null = null;

    private hal: HTMLElement;

    apiKey: string | undefined;
    cif:string = "";
    currentSessionId:string = "";
    currentUserName:string = "";

    private activeSender: Sender | null;
    private isFunctionCallInProgress = false;
    private lastSavedUserMessage = '';
    private lastSavedTranscriptItemId = '';
    private currentUtteranceTranscript = '';
    private activeResponseId: string | null = null;
    private hasSentGreeting = false;
    private sessionGreetingComplete = false;
    private agentSpeechStartedAt = 0;
    private pendingHistorySaves: Promise<void>[] = [];
    private micInputEnabled = false;
    private listeningEnabled = false;
    private userTurnValidated = false;
    private greetingBotMessageSaved = false;
    private savedUserMessageKeys = new Set<string>();
    private awaitingTranscript = false;
    private responseCreateInFlight = false;
    /** Persists through function-call round-trip; cleared only when the full turn ends. */
    private lastValidatedUserTranscript = '';
    /** Verbatim STT saved to chat — never replaced by LLM function-call paraphrase. */
    private lockedChatUserTranscript = '';
    private bargeInActive = false;
    // Filter for Offensive words
    offensiveFilter = new Filter({ placeHolder: '*' });

    private startIdleTimeout: DebouncedFunction<() => void>;

    constructor(private microphoneService: MicrophoneService) {
      super();
      this.socket = null;
      this.microphone = null;

      // unlike the socket and the mic setup, we can leave the tts context
      // sitting around
      this.scheduledPlaybackSources = new Set();
      this.startTime = -1;
      this.activeSender = null;
      this.hal = document.createElement('openai-hal');

      try {
        if (!AudioContextClass) {
          throw new Error('Web Audio API is not supported in this browser');
        }
        this.ttsContext = new AudioContextClass({
          latencyHint: 'interactive',
          sampleRate: TTS_SAMPLE_RATE,
        });

        // Resume the audio context to avoid browser restrictions
        if (this.ttsContext.state === 'suspended') {
          this.ttsContext.resume();
        }
        // Load the worklet processor
        //this.ttsContext.audioWorklet.addModule('assets/pcm-worklet-processor.js');
        // Create the worklet node
        //this.audioWorkletNode = new AudioWorkletNode(this.ttsContext, 'pcm-processor');
        // Connect the worklet node to the audio context
        // Configuration for audio capture
        //this.initMicStream();

        this.ttsAnalyser = createAnalyser(this.ttsContext);

        this.micContext = new AudioContextClass({ sampleRate: MIC_SAMPLE_RATE });

        sendVolumeUpdates(this.ttsAnalyser, this.hal, 'agent-volume');
      } catch {
        this.dispatch(AgentEvent.FAILED_SETUP);
      }

      this.startIdleTimeout = debounce(() => {}, 0);
    }

    private async initMicStream() {
      const audioConstraints = {
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          autoGainControl: true,
          noiseSuppression: true
        },
        video: false,
      };
      this.microphoneStream = await navigator.mediaDevices.getUserMedia(audioConstraints);
      const source = this.ttsContext.createMediaStreamSource(this.microphoneStream);
      if (this.audioWorkletNode) {
        source.connect(this.audioWorkletNode);
      }
    }

    static get observedAttributes() {
      return Object.values(ObservedAttributes);
    }

    private replaceIdleTimeout(timeoutString: string | null) {
      const timeout = Number(timeoutString ?? idleTimeoutMs);
      if (Number.isNaN(timeout) || timeout <= 0) return;
      this.startIdleTimeout.clear();
      this.startIdleTimeout = debounce(
        () => this.disconnect('idle timeout'),
        timeout
      );
    }

    private resetSessionState(): void {
      this.playbackGeneration++;
      this.playbackQueue = Promise.resolve();
      this.activeResponseId = null;
      this.hasSentGreeting = false;
      this.sessionGreetingComplete = false;
      this.agentSpeechStartedAt = 0;
      this.currentUtteranceTranscript = '';
      this.lastSavedTranscriptItemId = '';
      this.lastSavedUserMessage = '';
      this.isFunctionCallInProgress = false;
      this.micInputEnabled = false;
      this.listeningEnabled = false;
      this.userTurnValidated = false;
      this.greetingBotMessageSaved = false;
      this.savedUserMessageKeys.clear();
      this.awaitingTranscript = false;
      this.responseCreateInFlight = false;
      this.lastValidatedUserTranscript = '';
      this.lockedChatUserTranscript = '';
      this.bargeInActive = false;
    }

    private trackHistorySave(promise: Promise<void>): void {
      this.pendingHistorySaves.push(promise);
      void promise.finally(() => {
        this.pendingHistorySaves = this.pendingHistorySaves.filter((p) => p !== promise);
      });
    }

    async flushPendingHistorySaves(): Promise<void> {
      await Promise.allSettled(this.pendingHistorySaves);
    }

    private cancelActiveResponse(): void {
      if (!this.activeResponseId) {
        return;
      }
      this.sendClientMessage(
        JSON.stringify({
          type: 'response.cancel',
          response_id: this.activeResponseId,
        })
      );
    }

    private isGreetingProtected(): boolean {
      return !this.sessionGreetingComplete;
    }

    private pauseMicInput(): void {
      this.micInputEnabled = false;
      this.clearInputAudioBuffer();
    }

    /** Keep mic streaming so server VAD can detect barge-in during agent speech. */
    private keepMicOpenForBargeIn(): void {
      if (this.listeningEnabled && this.sessionGreetingComplete) {
        this.micInputEnabled = true;
      }
    }

    private clearInputAudioBuffer(): void {
      this.sendClientMessage(JSON.stringify({ type: 'input_audio_buffer.clear' }));
    }

    private enableListening(): void {
      this.listeningEnabled = true;
      this.micInputEnabled = true;
      this.clearInputAudioBuffer();
      debugLog.info('voice/mic', 'Listening enabled', this.micDiagnostics());
      this.scheduleMicCheck();
    }

    private clearUserTurnState(): void {
      this.userTurnValidated = false;
      this.currentUtteranceTranscript = '';
      this.lastValidatedUserTranscript = '';
      this.lockedChatUserTranscript = '';
      this.responseCreateInFlight = false;
      this.bargeInActive = false;
    }

    private requestAgentResponse(): void {
      if (
        this.responseCreateInFlight
        || this.activeResponseId
        || !this.listeningEnabled
        || !this.userTurnValidated
      ) {
        return;
      }
      this.responseCreateInFlight = true;
      this.sendClientMessage(JSON.stringify({ type: 'response.create' }));
    }

    private maybeCompleteGreetingAndEnableListening(): void {
      if (
        this.sessionGreetingComplete
        && this.scheduledPlaybackSources.size === 0
        && this.activeSender === null
        && !this.activeResponseId
      ) {
        this.enableListening();
      }
    }

    private handleUserStartedSpeaking(): void {
      if (!this.listeningEnabled || this.isGreetingProtected()) {
        this.clearInputAudioBuffer();
        return;
      }

      this.activeSender = Sender.User;
      this.startIdleTimeout.clear();

      const agentSpeaking =
        this.activeResponseId !== null || this.scheduledPlaybackSources.size > 0;

      if (!agentSpeaking) {
        return;
      }

      this.bargeInActive = true;
      this.userTurnValidated = false;
      this.responseCreateInFlight = false;
      this.currentUtteranceTranscript = '';
      this.cancelActiveResponse();
      this.stopTts();
      this.keepMicOpenForBargeIn();
    }

    private sendOpeningGreeting(): void {
      if (this.hasSentGreeting) {
        return;
      }
      this.hasSentGreeting = true;
      this.responseCreateInFlight = true;
      this.pauseMicInput();
      const name = this.currentUserName?.trim() || 'there';
      this.sendClientMessage(
        JSON.stringify({
          type: 'response.create',
          response: {
            output_modalities: ['audio'],
            instructions: `Give exactly one short greeting (two sentences maximum): welcome ${name} to GFH Investment Bank, introduce yourself as Sara, and offer help. Speak once — do not repeat welcome or say hello twice in the same response. Then stop and wait for the user.`,
          },
        })
      );
    }

    private logVoiceAgentEvent(eventType: string, payload: Record<string, unknown>): void {
      console.info('[voice-agent]', eventType, payload);
      void fetch(`${apiUrl}/log-transcription-error`, {
        method: 'POST',
        headers: getApiAuthHeaders({
          'Content-Type': 'application/json',
          'X-GFH-AI-CHATBOT-CIF': this.cif,
          'X-GFH-AI-CHATBOT-SESSION-ID': this.currentSessionId,
        }),
        body: JSON.stringify({
          transcription_error: {
            type: 'voice_agent_event',
            code: eventType,
            message: JSON.stringify(payload),
            param: null,
          },
        }),
      }).catch(() => {});
    }

    private refreshSessionIdentity(): void {
      const agentElement = document.querySelector('openai-realtime-agent');
      if (agentElement) {
        this.cif = agentElement.getAttribute('cif') || '';
        this.currentSessionId = agentElement.getAttribute('currentsessionid') || '';
        this.currentUserName = agentElement.getAttribute('currentusername') || this.currentUserName;
      }
    }

    private async saveSessionHistory(messages: ChatPayloadMessage[]): Promise<void> {
      if (!messages.length) {
        return;
      }

      this.refreshSessionIdentity();

      if (!this.cif || this.cif === 'null' || !this.currentSessionId) {
        console.error('❌ Cannot save chat history — missing cif or session id', {
          cif: this.cif,
          currentSessionId: this.currentSessionId,
        });
        return;
      }

      const savePromise = (async () => {
      const response = await fetch(`${apiUrl}/chat/session-history`, {
        method: 'POST',
        headers: getApiAuthHeaders({
          'Content-Type': 'application/json',
          'X-GFH-AI-CHATBOT-CIF': this.cif,
          'X-GFH-AI-CHATBOT-SESSION-ID': this.currentSessionId,
        }),
        body: JSON.stringify({
          isVoiceAgent: true,
          session_id: this.currentSessionId,
          messages,
        }),
      });

      if (!response.ok) {
        console.error('❌ Failed to save chat history', messages);
      } else {
        const userMessage = messages.find((m) => m.sender === 'user')?.message?.trim();
        if (userMessage) {
          this.lastSavedUserMessage = userMessage.toLowerCase();
        }
        console.log('✅ Chat history saved', messages);
      }
      })();

      this.trackHistorySave(savePromise);
      await savePromise;
    }

    private shouldSaveTranscript(text: string): boolean {
      const normalized = (text || '').trim();
      if (!normalized) {
        return false;
      }
      if (normalized === '....' || /^\.+$/.test(normalized)) {
        return false;
      }
      return true;
    }

    private isValidUserTranscript(text: string): boolean {
      const normalized = (text || '').trim();
      if (!this.shouldSaveTranscript(normalized)) {
        return false;
      }
      const wordCount = normalized.split(/\s+/).filter(Boolean).length;
      if (this.bargeInActive) {
        return normalized.length >= BARGE_IN_MIN_CHARS && wordCount >= 1;
      }
      if (wordCount < MIN_USER_TRANSCRIPT_WORDS && !normalized.includes('?')) {
        return false;
      }
      if (normalized.length < MIN_USER_TRANSCRIPT_CHARS && !normalized.includes('?')) {
        return false;
      }
      return true;
    }

    private isPhantomBotTranscript(text: string): boolean {
      const trimmed = (text || '').trim();
      if (!trimmed) {
        return true;
      }
      if (!this.userTurnValidated && !this.isFunctionCallInProgress) {
        return PHANTOM_BOT_PATTERNS.some((pattern) => pattern.test(trimmed));
      }
      return false;
    }

    private shouldSkipDuplicateUserSave(message: string, itemId?: string): boolean {
      const normalized = (message || '').trim().toLowerCase();
      if (!normalized || normalized === '....') {
        return true;
      }
      if (this.savedUserMessageKeys.has(normalized)) {
        return true;
      }
      const dedupeKey = `${itemId || 'no-item'}:${normalized}`;
      if (this.savedUserMessageKeys.has(dedupeKey)) {
        return true;
      }
      if (itemId && itemId === this.lastSavedTranscriptItemId) {
        return true;
      }
      if (!this.lastSavedUserMessage) {
        return false;
      }
      return normalized === this.lastSavedUserMessage;
    }

    private async saveUserUtterance(message: string, itemId?: string): Promise<void> {
      const cleaned = (message || '').trim();
      if (this.shouldSkipDuplicateUserSave(cleaned, itemId)) {
        return;
      }
      const normalized = cleaned.toLowerCase();
      const dedupeKey = `${itemId || 'no-item'}:${normalized}`;
      this.savedUserMessageKeys.add(dedupeKey);
      this.savedUserMessageKeys.add(normalized);
      if (itemId) {
        this.lastSavedTranscriptItemId = itemId;
      }
      await this.saveSessionHistory([{
        isVoiceAgent: true,
        sender: 'user',
        message: cleaned,
      }]);
    }

    /** Chat always shows verbatim STT — not the LLM paraphrase from ask_question. */
    private async ensureUserTranscriptInChat(): Promise<void> {
      const transcript = (this.lockedChatUserTranscript || this.lastValidatedUserTranscript || '').trim();
      if (!transcript) {
        return;
      }
      await this.saveUserUtterance(transcript, this.lastSavedTranscriptItemId || undefined);
    }

    private shouldSaveBotTranscript(transcript: string, isGreetingResponse = false): boolean {
      const text = (transcript || '').trim();
      if (!text || this.isPhantomBotTranscript(text)) {
        return false;
      }
      if (isGreetingResponse && !this.greetingBotMessageSaved) {
        return true;
      }
      return this.userTurnValidated || !!this.lastValidatedUserTranscript;
    }

    private dispatch(variant: AgentEvent, detail?: object) {
      this.dispatchEvent(
        detail ? new CustomEvent(variant, { detail }) : new CustomEvent(variant)
      );
    }

    sendClientMessage(message: ArrayBuffer | string): void {
      if (this.socket?.readyState === WebSocket.OPEN) {
        this.socket.send(message);
        // Would consumers like us to emit something when audio is sent?
        if (typeof message === 'string') {
          this.dispatch(AgentEvent.CLIENT_MESSAGE, JSON.parse(message));
        }
      }
    }

    private connectNodes({
      microphone,
      processor,
      analyser,
    }: UserMediaNodes): Result<UserMediaNodes, AgentEventDetail> {
      this.microphone = microphone;
      this.processor = processor;
      this.micAnalyser = analyser;

      if (!this.micContext || !this.ttsAnalyser || !this.ttsContext) {
        return Result.err({ variant: AgentEvent.FAILED_SETUP });
      }
      this.microphone.connect(this.micAnalyser);
      this.microphone.connect(this.processor);
      // WebKit only renders nodes reachable from the destination, so the worklet needs a path there.
      // Route it through a muted gain node so the mic is never played back through the speaker.
      this.micSink = this.micContext.createGain();
      this.micSink.gain.value = 0;
      this.processor.connect(this.micSink);
      this.micSink.connect(this.micContext.destination);
      this.ttsAnalyser.connect(this.ttsContext.destination);
      sendVolumeUpdates(this.micAnalyser, this.hal, 'user-volume');
      sendVolumeUpdates(this.ttsAnalyser, this.hal, 'agent-volume');

      return Result.ok({ microphone, processor, analyser });
    }

    private disconnectNodes() {
      tryDisconnect(this.microphone, this.micAnalyser);
      tryDisconnect(this.microphone, this.processor);
      tryDisconnect(this.processor, this.micContext?.destination);
      tryDisconnect(this.processor, this.micSink);
      tryDisconnect(this.micSink, this.micContext?.destination);
      this.micSink = undefined;
      tryDisconnect(this.ttsAnalyser, this.ttsContext?.destination);
    }

    private micDiagnostics(): Record<string, unknown> {
      const track = this.microphone?.mediaStream.getAudioTracks()[0];
      const settings = track?.getSettings?.();
      return {
        micContextState: this.micContext?.state,
        micContextSampleRate: this.micContext?.sampleRate,
        ttsContextState: this.ttsContext?.state,
        track: track
          ? {
              readyState: track.readyState,
              muted: track.muted,
              enabled: track.enabled,
              label: track.label,
              sampleRate: settings?.sampleRate,
              channelCount: settings?.channelCount,
            }
          : null,
        micInputEnabled: this.micInputEnabled,
        listeningEnabled: this.listeningEnabled,
        ...micStats,
      };
    }

    private watchMicTrack(): void {
      const track = this.microphone?.mediaStream.getAudioTracks()[0];
      if (!track) {
        debugLog.error('voice/mic', 'No audio track on the microphone stream');
        return;
      }
      track.addEventListener('mute', () => debugLog.warn('voice/mic', 'Mic track muted by the system', this.micDiagnostics()));
      track.addEventListener('unmute', () => debugLog.info('voice/mic', 'Mic track unmuted'));
      track.addEventListener('ended', () => debugLog.warn('voice/mic', 'Mic track ended', this.micDiagnostics()));
      if (this.micContext) {
        this.micContext.onstatechange = () => debugLog.info('voice/mic', `Mic audio context is ${this.micContext?.state}`);
      }
    }

    private scheduleListeningWatchdog(): void {
      setTimeout(() => {
        if (!this.socket || this.listeningEnabled) {
          return;
        }
        debugLog.error('voice/mic', 'Greeting finished but listening was never enabled', {
          sessionGreetingComplete: this.sessionGreetingComplete,
          scheduledPlaybackSources: this.scheduledPlaybackSources.size,
          activeSender: this.activeSender,
          activeResponseId: this.activeResponseId,
          ...this.micDiagnostics(),
        });
      }, 15000);
    }

    private scheduleMicCheck(): void {
      if (this.micCheckTimer) {
        clearTimeout(this.micCheckTimer);
      }
      const framesAtStart = micStats.workletFrames;
      const sentAtStart = micStats.framesSent;
      this.micCheckTimer = setTimeout(() => {
        this.micCheckTimer = null;
        if (!this.socket) {
          return;
        }
        const details = this.micDiagnostics();
        debugLog.setState('mic', details);
        if (micStats.workletFrames === framesAtStart) {
          debugLog.error('voice/mic', 'Mic is enabled but the audio processor produced no frames in 3s', details);
        } else if (micStats.framesSent === sentAtStart && this.micInputEnabled) {
          debugLog.error('voice/mic', 'Mic frames arrive but none were sent to the socket in 3s', details);
        } else {
          debugLog.info('voice/mic', 'Mic is streaming', details);
        }
      }, 3000);
    }

    private async suspendContext() {
      if (this.micContext) await this.micContext.suspend();
      if (this.ttsContext) await this.ttsContext.suspend();
    }

    private async resumeContext() {
      if (this.micContext) await this.micContext.resume();
      if (this.ttsContext) await this.ttsContext.resume();
    }

    private async closeContext() {
      if (this.micContext) await this.micContext.close();
      if (this.ttsContext) await this.ttsContext.close();
    }

    private checkAndHandleTtsPlaybackCompleted() {
      if (
        this.scheduledPlaybackSources.size === 0 &&
        this.activeSender === null
      ) {
        this.maybeCompleteGreetingAndEnableListening();
        if (this.listeningEnabled && !this.activeResponseId) {
          this.micInputEnabled = true;
        }
        this.startIdleTimeout();
      }
    }

    private clearActiveSenderIf(activeSender: Sender) {
      if (this.activeSender === activeSender) this.activeSender = null;
    }

    private createWavFromPcm16(pcm16: Int16Array, sampleRate = TTS_SAMPLE_RATE): ArrayBuffer {
      const numChannels = 1;
      const bytesPerSample = 2;
      const blockAlign = numChannels * bytesPerSample;
      const byteRate = sampleRate * blockAlign;
      const wavHeaderSize = 44;
      const dataSize = pcm16.length * bytesPerSample;

      const buffer = new ArrayBuffer(wavHeaderSize + dataSize);
      const view = new DataView(buffer);

      // "RIFF"
      view.setUint32(0, 0x52494646, false);
      view.setUint32(4, 36 + dataSize, true);
      view.setUint32(8, 0x57415645, false); // "WAVE"

      // "fmt "
      view.setUint32(12, 0x666d7420, false);
      view.setUint32(16, 16, true); // size of "fmt "
      view.setUint16(20, 1, true);  // PCM format
      view.setUint16(22, numChannels, true);
      view.setUint32(24, sampleRate, true);
      view.setUint32(28, byteRate, true);
      view.setUint16(32, blockAlign, true);
      view.setUint16(34, bytesPerSample * 8, true); // bits per sample

      // "data"
      view.setUint32(36, 0x64617461, false); // "data"
      view.setUint32(40, dataSize, true);

      // Write PCM16 samples
      const dataView = new Int16Array(buffer, 44);
      dataView.set(pcm16);

      return buffer;
    }

    private async playAudioFromBase64Delta(base64: string) {
      if (!this.ttsContext || !this.ttsAnalyser) {
        return;
      }

      if (this.ttsContext.state === 'suspended') {
        await this.ttsContext.resume();
      }

      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }

      const pcm16 = new Int16Array(bytes.buffer);

      // ✅ Use actual sample rate based on your testing
      const wavBuffer = this.createWavFromPcm16(pcm16, TTS_SAMPLE_RATE);

      const context = this.ttsAnalyser.context as AudioContext;

      // ✅ Important: await full decode before scheduling
      const audioBuffer = await context.decodeAudioData(wavBuffer.slice(0));

      const source = context.createBufferSource();
      source.buffer = audioBuffer;

      // 🔊 Add slight gain boost here
      const gainNode = context.createGain();
      gainNode.gain.value = 1.1; // tweak between 1.1 and 1.4 for louder voice

      // Audio routing
      source.connect(gainNode);
      gainNode.connect(this.ttsAnalyser);
      this.ttsAnalyser.connect(context.destination);

      const now = context.currentTime;
      const margin = 0.02; // 🔥 minimum safety gap

      if (this.startTime < now) {
        this.startTime = now + margin;
      }

      // ✅ Do not subtract padding anymore
      source.start(this.startTime);
      this.startTime += audioBuffer.duration;

      source.addEventListener('ended', () => {
        this.scheduledPlaybackSources.delete(source);
        this.checkAndHandleTtsPlaybackCompleted();
      });

      this.scheduledPlaybackSources.add(source);
    }

    private async handleSocketMessage(message: MessageEvent) {
      if (typeof message.data === 'string') {
        try {
          const data = JSON.parse(message.data);
          //console.log("Received Data :: ", data);

          switch (data.type) {
            case MessageType.UserStartedSpeaking:
              if (!this.listeningEnabled || this.isGreetingProtected()) {
                this.clearInputAudioBuffer();
                return;
              }
              this.awaitingTranscript = true;
              this.handleUserStartedSpeaking();
              break;
            case MessageType.UserStoppedSpeaking:
              if (!this.listeningEnabled || this.isGreetingProtected()) {
                this.awaitingTranscript = false;
                this.clearInputAudioBuffer();
              }
              break;
            case MessageType.ResponseCreated:
              this.activeResponseId = data.response?.id ?? this.activeResponseId;
              this.responseCreateInFlight = false;
              this.keepMicOpenForBargeIn();
              break;
            case MessageType.ResponseCancelled:
              this.activeResponseId = null;
              this.responseCreateInFlight = false;
              this.stopTts();
              this.keepMicOpenForBargeIn();
              break;
            case MessageType.TranscriptionFailed:
              this.awaitingTranscript = false;
              this.clearUserTurnState();
              this.clearInputAudioBuffer();
              this.cancelActiveResponse();
              /** Log Failed Transcription */
              const transcriptionError = data.error;
              debugLog.error('voice/stt', 'Azure could not transcribe the user speech; the turn was discarded', {
                code: transcriptionError?.code,
                type: transcriptionError?.type,
                message: transcriptionError?.message,
                transcriptionModel: resolveTranscriptionModel(),
              });
              if (transcriptionError) {
                try {
                  const response = await fetch(`${apiUrl}/log-transcription-error`, {
                    method: 'POST',
                    headers: getApiAuthHeaders({
                      'Content-Type': 'application/json',
                      'X-GFH-AI-CHATBOT-CIF': this.cif,
                      'X-GFH-AI-CHATBOT-SESSION-ID': this.currentSessionId
                    }),
                    body: JSON.stringify({
                      transcription_error: {
                        type: transcriptionError.type,
                        code: transcriptionError.code,
                        message: transcriptionError.message,
                        param: transcriptionError.param ?? null
                      }
                    })
                  });

                  if (!response.ok) {
                    console.error('Failed to log transcription error', await response.text());
                  } else {
                    console.log('Transcription error logged successfully');
                  }
                } catch (err) {
                  console.error('Error sending transcription error log:', err);
                }
              }
              break;
            case MessageType.EndOfThought:
              this.clearActiveSenderIf(Sender.User);
              this.awaitingTranscript = false;

              var transcript = '';

              if (currentAudioTranscriptModel.toString() === AudioTranscriptModel.GPT_4O_MINI_TRANSCRIBE.toString()) {
                const parsedTranscript = JSON.parse(data.transcript);
                transcript = parsedTranscript.text ?? parsedTranscript?.trim() ?? '';
              } else {
                transcript = data.transcript?.trim() ?? '';
              }

              var filteredQue = this.offensiveFilter.clean(transcript);
              const itemId = data.item_id ?? '';
              const chatTranscript = (transcript || filteredQue).trim();

              console.log('User Transcription Message ::: ', chatTranscript);

              this.currentUtteranceTranscript = chatTranscript;

              if (this.isValidUserTranscript(chatTranscript)) {
                this.userTurnValidated = true;
                this.lockedChatUserTranscript = chatTranscript;
                this.lastValidatedUserTranscript = chatTranscript;
                this.bargeInActive = false;
                await this.saveUserUtterance(chatTranscript, itemId);
                this.requestAgentResponse();
              } else {
                this.bargeInActive = false;
                this.clearUserTurnState();
                this.clearInputAudioBuffer();
                this.cancelActiveResponse();
                console.log('Discarded invalid/noise transcript:', filteredQue);
              }
              break;
            case MessageType.AgentStartedSpeaking:
              this.keepMicOpenForBargeIn();
              if (this.agentSpeechStartedAt === 0) {
                this.agentSpeechStartedAt = performance.now();
              }

              // Socket messages are handled concurrently, so decoding must be queued: otherwise the
              // "audio done" event can clear activeSender before the last delta finishes decoding,
              // and the late delta sets it back to Agent, which keeps listening disabled forever.
              this.activeSender = Sender.Agent;
              this.enqueuePlayback(() => this.playAudioFromBase64Delta(data.delta));
              break;
            case MessageType.AgentAudioDone:
              this.enqueuePlayback(async () => {
                this.clearActiveSenderIf(Sender.Agent);
                this.checkAndHandleTtsPlaybackCompleted();
              });
              break;
            case MessageType.SessionUpdated:
              this.sendOpeningGreeting();
              break;
            case MessageType.FunctionCallRequest:
              const functionCallarguments = data?.arguments;
              var question = JSON.parse(functionCallarguments)?.question;

              console.info("======Questions Asked to Function call: ", question);

              const functionName = data?.name;
              this.isFunctionCallInProgress = true;

              question = this.offensiveFilter.clean(question.trim());

              const chatUserTranscript =
                this.lockedChatUserTranscript
                || this.lastValidatedUserTranscript
                || this.currentUtteranceTranscript?.trim()
                || '';

              if (!chatUserTranscript) {
                console.warn('Ignoring function call — no validated user transcript', question);
                this.isFunctionCallInProgress = false;
                this.responseCreateInFlight = false;
                this.clearInputAudioBuffer();
                this.cancelActiveResponse();
                break;
              }

              await this.ensureUserTranscriptInChat();

              // LLM may normalize the question for the API; chat keeps verbatim STT only.
              const apiQuestion = (question || chatUserTranscript).trim();

              if (chatUserTranscript && question && chatUserTranscript.toLowerCase() !== question.toLowerCase()) {
                this.logVoiceAgentEvent('stt_function_call_mismatch', {
                  stt: chatUserTranscript,
                  functionCall: question,
                });
              }

              if (functionName === 'ask_question') {
                this.refreshSessionIdentity();

                const response = await fetch(`${apiUrl}/ask_question`, {
                  method: 'POST',
                  headers: getApiAuthHeaders({
                    'Content-Type': 'application/json',
                    'X-GFH-AI-CHATBOT-CIF': this.cif,
                    'X-GFH-AI-CHATBOT-SESSION-ID': this.currentSessionId
                  }),
                  body: JSON.stringify({
                    isVoiceAgent: true,
                    skip_user_history: true,
                    question: apiQuestion,
                    session_name: 'default'
                  })
                });

                const result = await response.json();
                console.log('Function Call Response:', result);

                const fullAnswer = result.answer ?? '';
                const { mode: voiceMode, speakThis: voiceSpeakContent } = prepareVoiceSpeakContent(
                  fullAnswer,
                  chatUserTranscript
                );

                const voiceResponseInstructions =
                  voiceMode === 'detailed'
                    ? 'The user wants full details. Use the answer field as source of truth — respond conversationally in your own words, but include every project, amount, and point. Do not skip items. Sound like a person explaining, not reading a script.'
                    : 'Use the answer field as source of truth. Give a concise conversational reply in your own words — include all project or product names and key numbers. About 15–30 seconds. Do not read verbatim.';

                // After Getting Response We need to Create Conversation Item
                this.sendClientMessage(
                  JSON.stringify({
                    type: 'conversation.item.create',
                    item: {
                      type: 'function_call_output',
                      call_id: data.call_id,
                      output: JSON.stringify({
                        answer: fullAnswer,
                        voice_delivery_mode: voiceMode,
                        user_question: chatUserTranscript,
                        summary_hint: voiceSpeakContent,
                        voice_note:
                          voiceMode === 'detailed'
                            ? 'User wants full details. Paraphrase the answer naturally — include everything, own words only.'
                            : 'User wants a concise answer. Paraphrase naturally — key names and numbers must be accurate.',
                      })
                    }
                  })
                );

                this.responseCreateInFlight = true;
                this.sendClientMessage(
                  JSON.stringify({
                    type: 'response.create',
                    response: {
                      output_modalities: ['audio'],
                      instructions: voiceResponseInstructions,
                    },
                  })
                );
              }

              break;
            case MessageType.ResponseDone:
              this.activeResponseId = null;
              this.responseCreateInFlight = false;
              const finishingGreeting =
                this.hasSentGreeting && !this.sessionGreetingComplete;
              if (finishingGreeting) {
                this.sessionGreetingComplete = true;
              }

              const responseOutput = data?.response?.output ?? [];
              const responseHasFunctionCall = responseOutput.some(
                (item: { type?: string }) => item?.type === 'function_call'
              );

              const agentTranscript = responseOutput.flatMap(
                (item: { content?: Array<{ type?: string; transcript?: string }> }) => item?.content ?? []
              ).find(
                (c: { type?: string; transcript?: string }) => c.type === 'output_audio' && c.transcript
              )?.transcript;

              if (agentTranscript) {
                this.refreshSessionIdentity();
                console.log('🗣️ Agent Final transcript:', agentTranscript, this.chatMessages);

                if (!this.isFunctionCallInProgress) {
                  if (this.shouldSaveBotTranscript(agentTranscript, finishingGreeting)) {
                    await this.saveSessionHistory([{
                      isVoiceAgent: true,
                      sender: 'bot',
                      message: agentTranscript,
                    }]);
                    if (finishingGreeting) {
                      this.greetingBotMessageSaved = true;
                    }
                  } else {
                    console.log('Skipping bot transcript save — phantom or no validated user turn');
                  }
                } else {
                  console.log('Skipping spoken bot transcript save — CRM answer stored by /ask_question');
                  this.chatMessages = [];
                }

                this.isFunctionCallInProgress = false;
              }

              if (!responseHasFunctionCall && !this.isFunctionCallInProgress) {
                this.clearUserTurnState();
              }

              if (finishingGreeting) {
                this.pauseMicInput();
                this.scheduleListeningWatchdog();
              }
              this.maybeCompleteGreetingAndEnableListening();
              break;
            default:
              break;
          }

          this.dispatch(AgentEvent.STRUCTURED_MESSAGE, data);
        } catch {
          this.dispatch(AgentEvent.UNKNOWN_MESSAGE);
        }
      }
    }

    private primeAudioOutput(): void {
      if (!this.ttsContext) {
        return;
      }

      if (this.ttsContext.state === 'suspended') {
        void this.ttsContext.resume();
      }

      try {
        const buffer = this.ttsContext.createBuffer(1, 1, this.ttsContext.sampleRate);
        const source = this.ttsContext.createBufferSource();
        source.buffer = buffer;
        source.connect(this.ttsContext.destination);
        source.start(0);
      } catch (error) {
        console.warn('Audio priming failed:', error);
      }
    }

    async connect(): Promise<void> {
      await Promise.resolve();

      this.resetSessionState();
      this.refreshSessionIdentity();

      this.primeAudioOutput();

      const kmMeta = localStorage.getItem('km_meta');
      const connectContext = {
        conversationMode,
        model: conversationMode === 'azure' ? AZURE_OPENAI_REALTIME_MODEL : OPENAI_REALTIME_MODEL,
        hasKmMeta: !!kmMeta,
        hasCif: !!this.cif,
        hasUserName: !!this.currentUserName,
        hasAccessToken: !!getAccessToken(),
        sessionId: this.currentSessionId,
      };
      debugLog.info('voice/connect', 'Connecting realtime voice agent', connectContext);
      debugLog.setState('voice', { ...connectContext, lastConnectAt: new Date().toISOString(), lastResult: 'connecting' });

      const url = REALTIME_WS_URL;
      if (!url) {
        debugLog.error('voice/connect', 'Realtime WebSocket URL is not configured', connectContext);
        this.dispatch(AgentEvent.NO_URL);
        return;
      }
      const configAttr = JSON.stringify(getSessionUpdateConfig(this.currentUserName, this.cif));
      if (!configAttr) {
        debugLog.error('voice/connect', 'Realtime session config could not be built', connectContext);
        this.dispatch(AgentEvent.NO_CONFIG);
        return;
      }

      if (!this.micContext || !this.ttsContext || !this.ttsAnalyser) {
        debugLog.error('voice/connect', 'Audio contexts are not ready', {
          ...connectContext,
          hasMicContext: !!this.micContext,
          hasTtsContext: !!this.ttsContext,
          hasTtsAnalyser: !!this.ttsAnalyser,
        });
        this.dispatch(AgentEvent.FAILED_SETUP);
        return;
      }

      if (!kmMeta) {
        debugLog.error('voice/connect', 'Voice agent missing km_meta realtime credentials', {
          ...connectContext,
          hint: connectContext.hasAccessToken
            ? 'Session exists but /auth/session returned no km_meta'
            : 'No session: the /auth/session login call failed or never ran (check the Network tab)',
        });
        debugLog.setState('voice', { ...connectContext, lastConnectAt: new Date().toISOString(), lastResult: 'no km_meta' });
        this.dispatch(AgentEvent.NO_KEY);
        return;
      }

      await this.resumeContext();
      micStats.workletFrames = 0;
      micStats.framesSkippedMicOff = 0;
      micStats.framesSent = 0;

      const [nodesResult, socketResult] = await Promise.all([
        getNodes(this.micContext, this.microphoneService).then((r) => r.andThen((nodes) => this.connectNodes(nodes))),
        openWebSocket(url + realtimeModel, kmMeta),
      ]);

      const nodesAndMicAndConfigString = pairResults(nodesResult, socketResult).andThen(([nodes, socket]) =>
        getConfigString(configAttr).map((configString) => [nodes, socket, configString])
      );


      match(
        {
          Ok: ([{ processor, analyser }, socket, configString]: [
            UserMediaNodes,
            WebSocket,
            string
          ]) => {
            const sendMicToSocket = sendMicTo(socket, () => this.micInputEnabled);

            socket.addEventListener('close', (event: CloseEvent) => {
              this.dispatch(AgentEvent.SOCKET_CLOSE, event);
              //processor.removeEventListener('audioprocess', sendMicToSocket);
              this.startIdleTimeout.clear();
            });
            socket.addEventListener(
              'message',
              this.handleSocketMessage.bind(this)
            );
            this.socket = socket;

            debugLog.info('voice/connect', 'Realtime voice agent connected', { ...connectContext, mic: this.micDiagnostics() });
            this.watchMicTrack();
            debugLog.setState('voice', { ...connectContext, lastConnectAt: new Date().toISOString(), lastResult: 'connected' });
            this.dispatch(AgentEvent.SOCKET_OPEN);
            this.sendClientMessage(configString);
            this.startIdleTimeout();

            //processor.addEventListener('audioprocess', sendMicToSocket);
            // ✅ Hook up microphone forwarding
            sendMicToSocket(processor);

            requestAnimationFrame(() => {
              sendVolumeUpdates(analyser, this.hal, 'user-volume');
              this.hal.setAttribute('orb-state', VoiceBotStatus.Active);
            });
          },
          Err: async ({ variant, detail }) => {
            debugLog.error('voice/connect', `Realtime voice agent failed to connect: ${variant}`, { ...connectContext, detail });
            debugLog.setState('voice', { ...connectContext, lastConnectAt: new Date().toISOString(), lastResult: `failed: ${variant}` });
            await this.disconnect();
            this.dispatch(variant, detail);
          },
        },
        nodesAndMicAndConfigString
      );
    }

    private enqueuePlayback(task: () => Promise<void>): void {
      const generation = this.playbackGeneration;
      this.playbackQueue = this.playbackQueue
        .then(() => (generation === this.playbackGeneration ? task() : undefined))
        .catch((error) => debugLog.warn('voice/tts', 'Agent audio playback step failed', error));
    }

    private stopTts() {
      this.playbackGeneration++;
      this.clearActiveSenderIf(Sender.Agent);
      this.scheduledPlaybackSources.forEach((source) => source.stop());
      this.scheduledPlaybackSources.clear();
      this.startTime = -1;
    }

    private async clearMicrophone() {
      this.microphone?.mediaStream.getTracks().forEach((t) => t.stop());
      this.microphone = null;

      if (this.microphoneStream) {
        this.microphoneStream.getTracks().forEach((track) => track.stop());
        this.microphoneStream = null;
      }

      if (this.audioWorkletNode) {

        if(this.audioWorkletNode?.port){
          this.audioWorkletNode.port.close();
        }

        this.audioWorkletNode.disconnect();
        this.audioWorkletNode = null;
      }
    }

    private clearSocket(reason?: string) {
      return new Promise<void>((resolve) => {
        if (this.socket?.readyState === WebSocket.OPEN) {
          this.socket.addEventListener('close', () => {
            this.socket = null;
            resolve();
          });

          if (reason) {
            this.socket.close(1000, reason);
          } else {
            this.socket.close(1000);
          }
        } else {
          this.socket = null;
          resolve();
        }
      });
    }

    async disconnect(reason?: string): Promise<void> {
      if (this.socket) {
        debugLog.info('voice/connect', `Disconnecting voice agent${reason ? `: ${reason}` : ''}`, this.micDiagnostics());
      }
      if (this.micCheckTimer) {
        clearTimeout(this.micCheckTimer);
        this.micCheckTimer = null;
      }
      await this.flushPendingHistorySaves();
      this.stopTts();
      await this.suspendContext();
      this.disconnectNodes();
      await this.clearSocket(reason);
      await this.clearMicrophone();
      this.hal.setAttribute('orb-state', VoiceBotStatus.Sleeping);
    }

    async restart() {
      await this.disconnect();
      this.connect();
    }

    async attributeChangedCallback(
      name: string,
      oldValue: string,
      newValue: string
    ) {
      switch (name) {
        case ObservedAttributes.config:
          if (!oldValue && newValue) {
            await this.connect();
          } else if (oldValue && !newValue) {
            await this.disconnect();
          } else if (newValue && this.socket) {
            this.restart();
          }
          break;

        case ObservedAttributes.idleTimeoutMs:
          this.replaceIdleTimeout(idleTimeoutMs);
          break;

        default:
          break;
      }
    }

    async disconnectedCallback() {
      await this.disconnect();
      await this.closeContext();
    }

    async connectedCallback() {
      this.hal.setAttribute('agent-volume', '0');
      this.hal.setAttribute('user-volume', '0');
      this.hal.setAttribute('orb-state', VoiceBotStatus.NotStarted);
      this.hal.setAttribute(
        'height',
        this.getAttribute(Attributes.height) ?? '300'
      );
      this.hal.setAttribute(
        'width',
        this.getAttribute(Attributes.width) ?? '400'
      );
      this.appendChild(this.hal);

      this.replaceIdleTimeout(30000 + '');
    }
  }
);

// Define global functions for connecting and disconnecting the agent
(globalThis as any).connectAgent = async (agent: HTMLElement) => {
  try {
    await (agent as any).connect();
    console.log('Agent connected');
  } catch (error) {
    console.error('Failed to connect:', error);
  }
};

(globalThis as any).disconnectAgent = async (agent: HTMLElement) => {
  try {
    await (agent as any).disconnect();
    console.log('Agent disconnected');
  } catch (error) {
    console.error('Failed to disconnect:', error);
  }
};

function getLang() {
  if (navigator?.languages !== undefined)
    return navigator.languages[0].split(/-|_/)[0]
  return 'en';//navigator.language;
}
